import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Graphics } from "@pixi/react";
import OverlayController, {
  OVERLAY_BACKGROUND_COLOR,
} from "./OverlayController.js";

const Overlay = forwardRef(
  (
    {
      dimensions,
      interactive = false,
      onPointerDown,
      name = "overlay",
      x = 0,
      y = 0,
      zIndex = 0,
      fadeDuration,
    },
    ref
  ) => {
    const overlayRef = useRef(null);
    const [backgroundAlpha, setBackgroundAlpha] = useState(0);

    // Create overlay controller instance
    const overlayControllerRef = useRef(null);
    if (!overlayControllerRef.current) {
      overlayControllerRef.current = new OverlayController(
        overlayRef,
        setBackgroundAlpha,
        fadeDuration
      );
    }

    // Memoize controller methods
    const fadeInBackground = useCallback((callback) => {
      overlayControllerRef.current.fadeInBackground(callback);
    }, []);

    const fadeOutBackground = useCallback((callback) => {
      overlayControllerRef.current.fadeOutBackground(callback);
    }, []);

    const setAlpha = useCallback((alpha) => {
      setBackgroundAlpha(alpha);
    }, []);

    // Memoize cursor calculation
    const cursor = useMemo(
      () => (interactive && onPointerDown ? "pointer" : "default"),
      [interactive, onPointerDown]
    );

    // Memoize draw function
    const draw = useCallback(
      (g) => {
        g.clear();
        g.beginFill(OVERLAY_BACKGROUND_COLOR, 1);
        g.drawRect(0, 0, dimensions.width, dimensions.height);
        g.endFill();
      },
      [dimensions.width, dimensions.height]
    );

    // Expose overlay controller methods via ref
    useImperativeHandle(
      ref,
      () => ({
        fadeInBackground,
        fadeOutBackground,
        setAlpha,
        graphics: overlayRef.current,
      }),
      [fadeInBackground, fadeOutBackground, setAlpha]
    );

    return (
      <Graphics
        ref={overlayRef}
        name={name}
        x={x}
        y={y}
        zIndex={zIndex}
        draw={draw}
        alpha={backgroundAlpha}
        eventMode={interactive ? "static" : "none"}
        pointerdown={onPointerDown}
        cursor={cursor}
      />
    );
  }
);

Overlay.displayName = "Overlay";

export default Overlay;
